import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useMergeStyles } from "@thkStore/Overrides/additionalStyles";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

interface OtherProductItem {
    fields: {
        image: string;
        brand: string;
        name: string;
        buttonLink: LinkFieldValue;
    };
}

const enum fields {
    heading = "heading",
    items = "items",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.heading]: string;
        [fields.items]: OtherProductItem[];
    };
    extendedStyles?: OtherProductsStyles;
}

export interface OtherProductsStyles {
    wrapper?: InjectableCss;
    header?: InjectableCss;
    headingText?: TypographyPresentationProps;
    grid?: InjectableCss;
    card?: InjectableCss;
    cardImage?: InjectableCss;
    cardBody?: InjectableCss;
    cardBrand?: TypographyPresentationProps;
    cardName?: TypographyPresentationProps;
    cardButton?: ButtonPresentationProps;
}

export const otherProductsStyles: OtherProductsStyles = {
    wrapper: {
        css: css`
            width: 100%;
            padding: 75px 0 65px;
            display: flex;
            flex-direction: column;
            gap: 60px;

            @media (max-width: 768px) {
                padding: 40px 0;
                gap: 20px;
            }
        `,
    },
    header: {
        css: css`
            display: block;
        `,
    },
    headingText: {
        css: css`
            font-size: 36px;
            font-weight: 800;
            margin: 0;

            p {
                font-size: 36px;
                font-weight: 800;
                margin: 0;
            }

            @media (max-width: 768px) {
                font-size: 22px;

                p {
                    font-size: 22px;
                }
            }
        `,
    },
    grid: {
        css: css`
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 16px;

            @media (max-width: 991px) {
                grid-template-columns: repeat(2, 1fr);
            }

            @media (max-width: 575px) {
                grid-template-columns: 1fr;
            }
        `,
    },
    card: {
        css: css`
            display: flex;
            flex-direction: column;
            overflow: hidden;
            border: 1px solid ${getColor("secondary.main")};

            &:hover {
                box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.15);
            }
        `,
    },
    cardImage: {
        css: css`
            width: 100%;
            aspect-ratio: 10 / 3;
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
        `,
    },
    cardBody: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 15px;
            flex: 1;
        `,
    },
    cardBrand: {
        css: css`
            font-size: 12px;
            font-weight: 600;
            color: ${getColor("primary.main")};
            margin-bottom: 4px;
        `,
    },
    cardName: {
        css: css`
            font-size: 15px;
            font-weight: 600;
            flex: 1;
        `,
    },
    cardButton: {
        css: css`
            width: 100%;
            margin-top: 8px;
            padding: 10px 16px;
            text-decoration: none;
            border-radius: 4px;
            font-weight: 600;
            font-size: 14px;
            color: ${({ theme }) => theme.colors.text.main} !important;
            background-color: ${({ theme }) => theme.colors.secondary.main};
            border: 1px solid ${({ theme }) => theme.colors.secondary.main};
            transition: background-color 0.2s, color 0.2s;
            display: inline-flex;

            &:hover {
                color: ${({ theme }) => theme.colors.primary.main} !important;
                background-color: ${({ theme }) => theme.colors.secondary.main} !important;

                span {
                    color: ${({ theme }) => theme.colors.primary.main} !important;
                }
            }

            svg {
                background-color: ${({ theme }) => theme.colors.primary.main};
                color: ${({ theme }) => theme.colors.primary.contrast};
                border-radius: 100%;
            }

            [class^="TypographyStyle-sc-"] {
                font-size: 14px;
                font-weight: 600;
                flex-grow: 1;
            }
        `,
    },
};

export const OtherProducts: FC<OwnProps> = ({ fields: widgetFields, extendedStyles }) => {
    const history = useHistory();
    const { isInShell } = useShellContext();
    const { links: buttonLinks } = useGetLinks(widgetFields.items || [], o => o.fields.buttonLink);
    const styles = useMergeStyles("otherProducts", otherProductsStyles, extendedStyles);

    const onClick = (url?: string) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }
            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    const items = widgetFields.items || [];

    return (
        <StyledWrapper className="other-products-wrapper" {...styles.wrapper}>
            {widgetFields.heading && (
                <StyledWrapper className="op-header" {...styles.header}>
                    <Typography as="h2" className="op-heading" {...styles.headingText}>
                        {parse(widgetFields.heading, parserOptions)}
                    </Typography>
                </StyledWrapper>
            )}

            {items.length > 0 && (
                <StyledWrapper className="op-grid" {...styles.grid}>
                    {items.map((item, index) => {
                        const link = buttonLinks[index];
                        const imgUrl = item.fields.image ? parseImageUrl(item.fields.image).url : "";

                        const cardImageStyles = {
                            css: css`
                                ${styles.cardImage?.css || ""}
                                ${imgUrl ? `background-image: url(${imgUrl});` : ""}
                            `,
                        };

                        return (
                            // eslint-disable-next-line react/no-array-index-key
                            <StyledWrapper key={index} className="op-card" {...styles.card}>
                                {imgUrl && (
                                    <StyledWrapper className="op-card-image" {...cardImageStyles} />
                                )}
                                <StyledWrapper className="op-card-body" {...styles.cardBody}>
                                    {item.fields.brand && (
                                        <Typography className="op-card-brand" {...styles.cardBrand}>
                                            {item.fields.brand}
                                        </Typography>
                                    )}
                                    {item.fields.name && (
                                        <Typography className="op-card-name" {...styles.cardName}>
                                            {item.fields.name}
                                        </Typography>
                                    )}
                                    {link?.url && (
                                        <Button
                                            className="op-card-button"
                                            icon={{ src: ChevronRight, position: "left" }}
                                            {...styles.cardButton}
                                            onClick={() => onClick(link.url)}
                                        >
                                            {translate("View Details")}
                                        </Button>
                                    )}
                                </StyledWrapper>
                            </StyledWrapper>
                        );
                    })}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: OtherProducts,
    definition: {
        group: "Basic",
        icon: "industry",
        displayName: "Other Products",
        fieldDefinitions: [
            {
                fieldType: "Contextual",
                name: fields.heading,
                displayName: "Heading",
                editorTemplate: "RichTextField",
                defaultValue: "",
                extendedConfig: { height: 100 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.items,
                displayName: "Items",
                editorTemplate: "ListField",
                getDisplay: ({ fields: { name } }) => name || "Product",
                defaultValue: [],
                tab: basicTab,
                fieldDefinitions: [
                    {
                        name: "image",
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "brand",
                        displayName: "Brand",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "name",
                        displayName: "Name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "buttonLink",
                        displayName: "Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
